/**
 * CohortScheduler — bulk unlock/close scheduler for existing quiz
 * enrollments. Admin picks a set of users + a set of quizzes, sets
 * unlock_at and/or close_at (or fires "unlock now" / "close now"),
 * and the system writes per-(user, quiz) schedule overrides.
 *
 * Feb 2026 · R13 (simpler variant per user choice 5b).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, BookOpen, LockOpen, Lock, CalendarClock, Search, RotateCcw } from "lucide-react";

export default function CohortScheduler({ workspaceId }) {
  const [users, setUsers] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedQuizzes, setSelectedQuizzes] = useState(new Set());
  const [userFilter, setUserFilter] = useState("");
  const [quizFilter, setQuizFilter] = useState("");
  const [action, setAction] = useState("schedule");
  const [unlockAt, setUnlockAt] = useState("");
  const [closeAt, setCloseAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [uRes, qRes, sRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/members`),
        api.get(`/workspaces/${workspaceId}/lms/all-quizzes`),
        api.get(`/workspaces/${workspaceId}/lms/cohort/schedules`),
      ]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : uRes.data?.members || []);
      setQuizzes(Array.isArray(qRes.data) ? qRes.data : []);
      setSchedules(Array.isArray(sRes.data) ? sRes.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const filteredUsers = useMemo(() =>
    users.filter(u => {
      const q = userFilter.toLowerCase().trim();
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q);
    }), [users, userFilter]);
  const filteredQuizzes = useMemo(() =>
    quizzes.filter(qz => {
      const q = quizFilter.toLowerCase().trim();
      if (!q) return true;
      return (qz.title || "").toLowerCase().includes(q);
    }), [quizzes, quizFilter]);

  const toggleUser = (uid) => setSelectedUsers(s => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const toggleQuiz = (qid) => setSelectedQuizzes(s => { const n = new Set(s); n.has(qid) ? n.delete(qid) : n.add(qid); return n; });
  const selectAllUsers = () => setSelectedUsers(new Set(filteredUsers.map(u => u.user_id || u.id)));
  const clearUsers = () => setSelectedUsers(new Set());
  const selectAllQuizzes = () => setSelectedQuizzes(new Set(filteredQuizzes.map(q => q.id)));
  const clearQuizzes = () => setSelectedQuizzes(new Set());

  const apply = async () => {
    if (selectedUsers.size === 0) return toast.error("Select at least one user");
    if (selectedQuizzes.size === 0) return toast.error("Select at least one quiz");
    if (action === "schedule" && !unlockAt && !closeAt) return toast.error("Provide unlock or close time");

    const payload = {
      user_ids: [...selectedUsers],
      quiz_ids: [...selectedQuizzes],
      action,
      unlock_at: unlockAt ? new Date(unlockAt).toISOString() : null,
      close_at: closeAt ? new Date(closeAt).toISOString() : null,
    };
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/lms/cohort/bulk-schedule`, payload);
      toast.success(`Applied to ${data.updated} (user × quiz) pair${data.updated === 1 ? "" : "s"}`);
      // Reset partial state, keep selections so admin can chain-apply
      if (action === "schedule") { setUnlockAt(""); setCloseAt(""); }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };

  const totalPairs = selectedUsers.size * selectedQuizzes.size;
  const nowStr = new Date().toISOString().slice(0, 16); // local ISO for input min

  return (
    <div className="space-y-4" data-testid="cohort-scheduler">
      <div className="text-xs text-muted-foreground">
        Bulk unlock or close quizzes for a set of users. Overrides take priority over any workspace-default or enrollment schedule.
      </div>

      {/* Action bar */}
      <div className="border p-3 space-y-3 bg-neutral-50/40">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="h-9 rounded-none" data-testid="cohort-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="schedule">Schedule (set unlock/close)</SelectItem>
                <SelectItem value="unlock_now">Unlock now</SelectItem>
                <SelectItem value="close_now">Close now (revoke access)</SelectItem>
                <SelectItem value="clear">Clear cohort override</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {action === "schedule" && (
            <>
              <div>
                <Label className="text-xs">Unlock at</Label>
                <Input type="datetime-local" min={nowStr} value={unlockAt} onChange={e => setUnlockAt(e.target.value)} className="h-9 rounded-none" data-testid="cohort-unlock-at" />
              </div>
              <div>
                <Label className="text-xs">Close at</Label>
                <Input type="datetime-local" value={closeAt} onChange={e => setCloseAt(e.target.value)} className="h-9 rounded-none" data-testid="cohort-close-at" />
              </div>
            </>
          )}
          <div className="flex items-end">
            <Button className="rounded-none w-full h-9" onClick={apply} disabled={busy || totalPairs === 0} data-testid="cohort-apply">
              {actionIcon(action)}
              {busy ? "Applying…" : `Apply to ${totalPairs} pair${totalPairs === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Selection grids */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Users */}
        <div className="border">
          <div className="border-b p-2 flex items-center gap-2 bg-neutral-50/60">
            <Users className="w-4 h-4 text-neutral-600" />
            <div className="text-xs font-semibold flex-1">Users ({selectedUsers.size}/{filteredUsers.length})</div>
            <button onClick={selectAllUsers} className="text-[10px] uppercase tracking-widest text-blue-600 hover:underline" data-testid="cohort-select-all-users">All</button>
            <span className="text-neutral-300">|</span>
            <button onClick={clearUsers} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:underline">Clear</button>
          </div>
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-2.5 text-neutral-400" />
              <Input placeholder="Search name or email…" value={userFilter} onChange={e => setUserFilter(e.target.value)} className="h-7 rounded-none pl-6 text-xs" data-testid="cohort-user-filter" />
            </div>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {loading ? <div className="p-3 text-xs text-muted-foreground">Loading…</div>
              : filteredUsers.length === 0 ? <div className="p-3 text-xs text-muted-foreground italic">No users match</div>
              : filteredUsers.map(u => {
                const uid = u.user_id || u.id;
                return (
                  <label key={uid} className={`flex items-center gap-2 p-2 border-b text-xs cursor-pointer hover:bg-blue-50/50 ${selectedUsers.has(uid) ? "bg-blue-50" : ""}`} data-testid={`cohort-user-${uid}`}>
                    <input type="checkbox" checked={selectedUsers.has(uid)} onChange={() => toggleUser(uid)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.name || u.email}</div>
                      {u.email && u.name && <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>}
                    </div>
                  </label>
                );
              })}
          </div>
        </div>

        {/* Quizzes */}
        <div className="border">
          <div className="border-b p-2 flex items-center gap-2 bg-neutral-50/60">
            <BookOpen className="w-4 h-4 text-neutral-600" />
            <div className="text-xs font-semibold flex-1">Quizzes ({selectedQuizzes.size}/{filteredQuizzes.length})</div>
            <button onClick={selectAllQuizzes} className="text-[10px] uppercase tracking-widest text-blue-600 hover:underline" data-testid="cohort-select-all-quizzes">All</button>
            <span className="text-neutral-300">|</span>
            <button onClick={clearQuizzes} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:underline">Clear</button>
          </div>
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-2.5 text-neutral-400" />
              <Input placeholder="Search quiz title…" value={quizFilter} onChange={e => setQuizFilter(e.target.value)} className="h-7 rounded-none pl-6 text-xs" data-testid="cohort-quiz-filter" />
            </div>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {loading ? <div className="p-3 text-xs text-muted-foreground">Loading…</div>
              : filteredQuizzes.length === 0 ? <div className="p-3 text-xs text-muted-foreground italic">No quizzes match</div>
              : filteredQuizzes.map(qz => (
                <label key={qz.id} className={`flex items-center gap-2 p-2 border-b text-xs cursor-pointer hover:bg-blue-50/50 ${selectedQuizzes.has(qz.id) ? "bg-blue-50" : ""}`} data-testid={`cohort-quiz-${qz.id}`}>
                  <input type="checkbox" checked={selectedQuizzes.has(qz.id)} onChange={() => toggleQuiz(qz.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{qz.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {qz.is_standalone ? "Standalone" : "Course quiz"} · {qz.prometric_mode || "off"}
                    </div>
                  </div>
                </label>
              ))}
          </div>
        </div>
      </div>

      {/* Existing cohort schedules */}
      {schedules.length > 0 && (
        <div className="border">
          <div className="border-b p-2 text-xs font-semibold bg-neutral-50/60 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-neutral-600" />
            Active cohort overrides ({schedules.length})
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Quiz</th>
                  <th className="text-left p-2">Unlock at</th>
                  <th className="text-left p-2">Close at</th>
                  <th className="text-left p-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {schedules.slice(0, 100).map((s, i) => {
                  const u = users.find(x => (x.user_id || x.id) === s.user_id);
                  const qz = quizzes.find(x => x.id === s.quiz_id);
                  return (
                    <tr key={i} className="border-t">
                      <td className="p-2">{u?.name || u?.email || s.user_id.slice(0, 8)}</td>
                      <td className="p-2">{qz?.title || s.quiz_id.slice(0, 8)}</td>
                      <td className="p-2 font-mono text-[11px]">{s.unlock_at ? new Date(s.unlock_at).toLocaleString() : "—"}</td>
                      <td className="p-2 font-mono text-[11px]">{s.close_at ? new Date(s.close_at).toLocaleString() : "—"}</td>
                      <td className="p-2 text-[10px] text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function actionIcon(action) {
  const cls = "w-4 h-4 mr-1";
  if (action === "unlock_now") return <LockOpen className={cls} />;
  if (action === "close_now") return <Lock className={cls} />;
  if (action === "clear") return <RotateCcw className={cls} />;
  return <CalendarClock className={cls} />;
}
